import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  BookOpen,
  Loader2,
  Search,
  Pencil,
  Trash2,
  ExternalLink,
  AlertCircle,
  GripVertical,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter,
} from "../../components/ui/dialog";
import useCaseStudies from "../../hooks/useCaseStudies";
import { useAuth } from "../../context/AuthContext";

const CaseStudyList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("case_studies.create");
  const canUpdate = hasPermission("case_studies.update");
  const canDelete = hasPermission("case_studies.delete");
  const canReorder = hasPermission("case_studies.reorder");
  const { caseStudies, loading, deleteCaseStudy: apiDeleteCaseStudy, reorderCaseStudies, search, setSearch } = useCaseStudies();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(caseStudies);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    reorderCaseStudies(items);
  };

  const handleDelete = async () => {
    if (itemToDelete === null || itemToDelete === undefined) return;

    try {
      setIsDeleting(true);
      const success = await apiDeleteCaseStudy(itemToDelete);
      if (success) {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-primary/10 text-primary rounded-2xl">
              <BookOpen size={28} />
            </span>
            Case Studies
          </h1>
          <p className="text-slate-500 mt-1 font-medium">
            Manage your portfolio case studies and detailed stories.
          </p>
        </div>
        {canCreate && <Button
          onClick={() => navigate("/case-studies/add")}
          className="bg-primary hover:bg-primary/90 text-secondary rounded-2xl px-6 py-6 shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
        >
          <Plus size={20} className="mr-2" />
          New Case Study
        </Button>}
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <Input
            placeholder="Search by title or URL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-2xl border-slate-200 focus-visible:ring-primary"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-dashed border-slate-300">
          <Loader2 className="animate-spin text-primary mb-4" size={40} />
          <p className="text-slate-500 font-medium">Loading case studies...</p>
        </div>
      ) : caseStudies.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm">
          <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-400">
            <BookOpen size={40} />
          </div>
          <h3 className="text-xl font-bold text-slate-800">
            No case studies yet
          </h3>
          <p className="text-slate-500 mb-8 max-w-xs mx-auto">
            Create your first case study to showcase depth and results.
          </p>
          {canCreate && <Button
            onClick={() => navigate("/case-studies/add")}
            className="bg-primary hover:bg-primary/90 text-secondary rounded-xl"
          >
            Create Your First Case Study
          </Button>}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Page URL</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <Droppable droppableId="casestudies-droppable">
                {(provided) => (
                  <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                    {caseStudies.map((item, index) => (
                      <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index} isDragDisabled={!canReorder}>
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              display: snapshot.isDragging ? 'table' : 'table-row',
                              tableLayout: 'fixed',
                              backgroundColor: snapshot.isDragging ? '#f8fafc' : 'transparent',
                            }}
                            className="hover:bg-slate-50/50 transition-colors"
                          >
                            <TableCell className="w-[40px]">
                              {canReorder && <div
                                {...provided.dragHandleProps}
                                className="cursor-grab p-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing"
                              >
                                <GripVertical size={16} />
                              </div>}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-semibold text-slate-900">
                                  {item.sort_data?.title}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {item.sort_data?.sub_title}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 font-mono text-sm text-slate-600">
                                /{item.page_url}
                                <a
                                  href={`https://five-online-eight.vercel.app/case-studies/${item.page_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  <ExternalLink size={14} />
                                </a>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                {item.sort_data?.industry_id || "N/A"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-serif">
                              <div className="flex justify-end gap-1">
                                {canUpdate && <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/case-studies/edit/${item.id}`)
                                  }
                                  className="text-slate-500 hover:text-primary hover:bg-primary/10 h-8 w-8 p-0 rounded-full"
                                >
                                  <Pencil size={16} />
                                </Button>}
                                {canDelete && <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setItemToDelete(item.id);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-slate-500 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                                >
                                  <Trash2 size={16} />
                                </Button>}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </div>
        </DragDropContext>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="text-red-500" size={24} />
            </div>
            <DialogTitle className="text-center text-xl font-bold text-slate-900">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-center text-slate-500 mt-2">
              Are you sure you want to delete this case study? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-2xl border-slate-200"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseStudyList;
